// crewbit-admin/src/components/schedule/EntryTile.tsx
//
// Schedule Module Phase C C1.5 — canonical tile renderer for Day + Week views.
// Body parallel to mobile crewbit-mobile/components/schedule/EntryTile.tsx
// modulo runtime per feedback_admin_pair_must_match_mobile_body.md.
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §2.6.1.

import { palette, urgency } from '../../styles/tokens';
import { instantToBusinessParts } from './scheduleHelpers';
import type { ScheduleEntry } from '../../types/schedule';

const WARNING_BG = '#FEF3C7';

export interface EntryTileProps {
  entry: ScheduleEntry;
  onTap: (entryId: string) => void;
  onLongPress?: (entryId: string) => void;
  density?: 'compact' | 'normal';
  draggable?: boolean;
  industryAccent?: string;
  vocab?: { primary_noun?: string };
  isConflicting?: boolean;
  showRecurringBadge?: boolean;
  dropOffLabel?: string;
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd?: (e: React.DragEvent<HTMLDivElement>) => void;
}

function formatTimeRange(startIso: string, endIso: string): string {
  // A2 P4 — stored instants rendered in business tz, not device tz.
  const fmt = (iso: string) => {
    const { hour: h, minute: m } = instantToBusinessParts(iso);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };
  return `${fmt(startIso)}–${fmt(endIso)}`;
}

function statusBadgeText(status: string): string | null {
  if (status === 'in_progress') return 'In progress';
  if (status === 'completed') return 'Done';
  if (status === 'cancelled') return 'Cancelled';
  if (status === 'no_show') return 'No-show';
  return null;
}

function tileStatusStyle(status: string): React.CSSProperties {
  if (status === 'in_progress') return { backgroundColor: palette.accentWash };
  if (status === 'completed') return { opacity: 0.7 };
  if (status === 'cancelled') return { opacity: 0.5 };
  if (status === 'no_show') return { backgroundColor: WARNING_BG };
  return {};
}

export function EntryTile({
  entry,
  onTap,
  onLongPress,
  density = 'normal',
  draggable,
  industryAccent,
  vocab,
  isConflicting,
  showRecurringBadge,
  dropOffLabel,
  onDragStart,
  onDragEnd,
}: EntryTileProps) {
  const title = entry.title || vocab?.primary_noun || 'Untitled';
  const subtitle = entry.notes ?? null;
  const statusBadge = statusBadgeText(entry.status);
  const isCompact = density === 'compact';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onTap(entry.id)}
      onContextMenu={onLongPress ? (e) => { e.preventDefault(); onLongPress(entry.id); } : undefined}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTap(entry.id); }
      }}
      aria-label={`${title} ${formatTimeRange(entry.start_at, entry.end_at)}${entry.status !== 'scheduled' ? ` ${entry.status}` : ''}`}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      data-entry-id={entry.id}
      data-testid="entry-tile"
      style={{
        backgroundColor: palette.white,
        borderRadius: 12,
        borderLeft: `4px solid ${industryAccent ?? palette.accent}`,
        border: isConflicting ? `1px solid ${urgency.warning}` : undefined,
        padding: isCompact ? '6px 12px' : '10px 12px',
        minHeight: isCompact ? 56 : 80,
        cursor: draggable ? 'grab' : 'pointer',
        userSelect: 'none',
        ...tileStatusStyle(entry.status),
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: palette.g600 }}>
          {formatTimeRange(entry.start_at, entry.end_at)}
        </span>
        {statusBadge ? (
          <span style={{ fontSize: 11, fontWeight: 600, color: palette.g500, textTransform: 'uppercase', letterSpacing: 0.3 }}>
            {statusBadge}
          </span>
        ) : null}
      </div>

      <div
        style={{
          fontSize: 15, fontWeight: 600, color: palette.g800, marginTop: 2,
          textDecoration: entry.status === 'cancelled' ? 'line-through' : undefined,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
      >
        {title}
      </div>

      {!isCompact && subtitle ? (
        <div style={{ fontSize: 12, color: palette.g500, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {subtitle}
        </div>
      ) : null}

      {!isCompact && (showRecurringBadge || dropOffLabel || isConflicting) ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4, fontSize: 11, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' }}>
          {showRecurringBadge ? <span style={{ color: palette.g500 }}>↻ Recurring</span> : null}
          {dropOffLabel ? <span style={{ color: palette.g500 }}>{dropOffLabel}</span> : null}
          {isConflicting ? <span style={{ color: urgency.warning }}>⚠ Conflict</span> : null}
        </div>
      ) : null}
    </div>
  );
}
